import DwhLogger from '@media-platform/dwh-logger';
import { DWH_EVENTS } from '@media-platform/dwh-logger/build/constants';
import type { DWHConfig, DWHParams, HitEventParams, AttachChildrenParams, } from '@media-platform/dwh-logger/build/types';
export interface IVirtualGallerySingleOptions {
    type: 'single';
    photo: string;
}
export interface IVirtualGallerySlideOptions {
    type: 'slide';
    photo: string;
    gallery: number;
}
export interface IVirtualGalleryAnyOptions {
    type: 'finish' | 'splash';
}
export type IVirtualGalleryOptions = IVirtualGallerySingleOptions | IVirtualGallerySlideOptions | IVirtualGalleryAnyOptions;
export const getDWHLogger = () => typeof window !== 'undefined' ? window.DWHLogger : null;
export const initDWHLogger = async () => {
    const xray = typeof window !== 'undefined' ? await window.xray : null;
    const dwhLogger = typeof window !== 'undefined'
        ? window.xrayOptions && window.xrayOptions.dwhLogger
        : null;
    if (!xray || !dwhLogger) {
        return;
    }
    const loggerInstance = new DwhLogger({ xray });
    loggerInstance.initialize({
        config: {
            options: dwhLogger.options,
            asyncTarget: 'init',
        },
    });
};
const withDWHLogger = <T extends any[], R>(fn: (logger: DwhLogger, ...args: T) => R) => (...args: T): R | void => {
    const loggerInstance = getDWHLogger();
    if (loggerInstance) {
        return fn.call(null, loggerInstance, ...args);
    }
    else {
        console.error('Нет экземпляра DWHLogger');
    }
};
export const reinitDWHLogger = (config: any) => {
    const loggerInstance = typeof window !== 'undefined' && window.DWHLogger;
    if (loggerInstance) {
        loggerInstance.reinitialize(config);
    }
};
export const attachChildrenToDWH = withDWHLogger((loggerInstance: DwhLogger, config: AttachChildrenParams) => {
    loggerInstance.attachChildren(config);
});
/**
 * Обновляет опции логгера и переинициализирует. Если передан доп. элемент, то на нем инициализирует отдельно
 * @param data Объект с параметрами
 * @param url url текущей страницы, который попадет в параметры
 * @param element Элемент, в котором нужно будет проинициализировать элементы с data-logger
 */
export const updateDWH = withDWHLogger((loggerInstance: DwhLogger, data: Record<string, string | number>, element?: Element | null, config?: Omit<DWHConfig, 'options' | 'excludeSelector'>) => {
    const { entity_content_subtype, entity_type, entity_id, brandsafety } = data;
    const { xrayOptions } = window;
    const { updateLoggerOptions, options } = xrayOptions.dwhLogger;
    if (updateLoggerOptions) {
        updateLoggerOptions({
            entity_type,
            entity_id,
            brandsafety,
            entity_content_subtype,
            url: window.location.href,
            search: window.location.search,
        });
        loggerInstance.reinitialize({
            root: document.body,
            config: {
                options,
                excludeSelector: element ? '.js-dwh-logger-exclude' : undefined,
                ...config,
            },
        });
        if (element) {
            loggerInstance.attachChildren({
                root: element as HTMLElement,
                config: {
                    options,
                },
            });
        }
    }
});
export const hitDWHEvent = withDWHLogger((loggerInstance: DwhLogger, options: HitEventParams) => {
    loggerInstance.hitEvent(options);
});
export const updateDWHBaseParams = withDWHLogger((loggerInstance: DwhLogger, options: DWHParams) => {
    loggerInstance.updateBaseParams(options);
});
export const getVirtualURL = (pageURL: string, options: IVirtualGalleryOptions) => {
    const galleryPath = `${pageURL}virtual_gallery`;
    let url: string;
    switch (options.type) {
        case 'single':
            url = `${galleryPath}/photo/${options.photo}/`;
            break;
        case 'slide':
            url = `${galleryPath}/${options.gallery}/${options.photo}/`;
            break;
        default:
            url = `${galleryPath}/${options.type}/`;
    }
    return url;
};
export const hitVirtualGallery = (pageURL: string, options: IVirtualGalleryOptions): void => {
    const url = getVirtualURL(pageURL, options);
    const countersParams = { url, async_target: 'gallery' };
    hitDWHEvent({
        type: DWH_EVENTS.PAGE_OPEN,
        params: countersParams,
    });
};
export const logElementWithNewURL = (root: Element | null, url: string = window.location.href, preventPageOpen?: boolean) => {
    if (root) {
        reinitDWHLogger({
            root: root as HTMLElement,
            config: {
                preventPageOpen,
                options: {
                    url,
                },
            },
        });
    }
};
export const hitDWHEventPushNotifications = (type: string) => hitDWHEvent({
    type: DWH_EVENTS.ELEMENT_CLICK,
    params: {
        element_type: type,
    },
});
